import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ThemeProvider,
  getContrastRatio,
  useTheme,
} from "@mui/material/styles";
import { HEX_COLOR_PATTERN } from "../../../affiliate/affiliateService";
import {
  createAffiliateTheme,
  readableTextOn,
} from "../../../affiliate/createAffiliateTheme";
import { AffiliateTheme } from "../../../affiliate/types";
import { AffiliateAuthShell } from "../../auth/components/AuthShell";
import { SignInForm } from "../../auth/components/AuthForms";
import {
  AffiliateDetail,
  BrandingDraft,
  PublishBrandingResult,
  affiliateAdminApi,
  apiErrorMessage,
} from "./affiliateAdminApi";

// Mirrors the API limits in BrandingStorageOptions and BrandingImageValidator.
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const FAVICON_TYPES = ["image/png", "image/x-icon", "image/vnd.microsoft.icon"];
const MAX_LOGO_BYTES = 512_000;
const MAX_FAVICON_BYTES = 100_000;
const MIN_TEXT_CONTRAST = 4.5;
const MIN_LARGE_TEXT_CONTRAST = 3;

const noop = () => undefined;

function draftFrom(detail: AffiliateDetail): BrandingDraft {
  const theme = detail.activeTheme;
  return {
    tagline: theme?.tagline ?? "",
    termsUrl: theme?.termsUrl ?? "",
    supportEmail: theme?.supportEmail ?? "",
    primary: theme?.primary ?? "#1A365D",
    background: theme?.background ?? "#FFFFFF",
    text: theme?.text ?? "#1F2933",
  };
}

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const valid = HEX_COLOR_PATTERN.test(value);

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <input
        type="color"
        aria-label={`${label} picker`}
        value={valid ? value : "#000000"}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
        style={{
          width: 48,
          height: 40,
          padding: 0,
          border: "1px solid rgba(0,0,0,0.23)",
          borderRadius: 4,
          background: "none",
          cursor: "pointer",
        }}
      />
      <TextField
        label={label}
        size="small"
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value.trim())}
        error={!valid}
        helperText={valid ? undefined : "Use the #RRGGBB format"}
        slotProps={{ htmlInput: { maxLength: 7, spellCheck: false } }}
      />
    </Stack>
  );
}

function ImagePicker({
  label,
  helper,
  accept,
  previewUrl,
  onSelect,
  background = "grey.100",
}: {
  label: string;
  helper: string;
  accept: string;
  previewUrl: string | null;
  onSelect: (file: File | null) => void;
  /** Plate colour behind the thumbnail: a white logo is invisible on a light one. */
  background?: string;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 112,
            height: 56,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: background,
            overflow: "hidden",
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`${label} preview`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              None
            </Typography>
          )}
        </Box>
        <Button component="label" variant="outlined" size="small">
          Choose file
          <input
            hidden
            type="file"
            accept={accept}
            onChange={(event) => {
              onSelect(event.target.files?.[0] ?? null);
              event.target.value = "";
            }}
          />
        </Button>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.5 }}
      >
        {helper}
      </Typography>
    </Box>
  );
}

function ContrastChip({
  label,
  ratio,
  minimum,
}: {
  label: string;
  ratio: number | null;
  minimum: number;
}) {
  if (ratio === null) {
    return null;
  }
  const passes = ratio >= minimum;
  return (
    <Chip
      size="small"
      color={passes ? "success" : "warning"}
      variant="outlined"
      label={`${label}: ${ratio.toFixed(1)}:1 ${passes ? "✓" : `(needs ${minimum}:1)`}`}
    />
  );
}

export function BrandingEditor({
  detail,
  onPublished,
}: {
  detail: AffiliateDetail;
  onPublished: () => void;
}) {
  const baseTheme = useTheme();
  const [draft, setDraft] = useState<BrandingDraft>(() => draftFrom(detail));
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [removeFavicon, setRemoveFavicon] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [lastPublished, setLastPublished] =
    useState<PublishBrandingResult | null>(null);

  const logoObjectUrl = useObjectUrl(logoFile);
  const faviconObjectUrl = useObjectUrl(faviconFile);
  const logoPreviewUrl = logoObjectUrl ?? detail.activeTheme?.logoUrl ?? null;
  const faviconPreviewUrl =
    faviconObjectUrl ??
    (removeFavicon ? null : (detail.activeTheme?.faviconUrl ?? null));

  const update = (field: keyof BrandingDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const colorsValid = [draft.primary, draft.background, draft.text].every(
    (color) => HEX_COLOR_PATTERN.test(color),
  );
  const textContrast = colorsValid
    ? getContrastRatio(draft.text, draft.background)
    : null;
  const buttonContrast = colorsValid
    ? getContrastRatio(draft.primary, readableTextOn(draft.primary))
    : null;
  const accentContrast = colorsValid
    ? getContrastRatio(draft.primary, draft.background)
    : null;

  const previewTheme = useMemo(
    () => (colorsValid ? createAffiliateTheme(baseTheme, draft) : null),
    [baseTheme, colorsValid, draft],
  );

  const previewBranding: AffiliateTheme = {
    name: detail.name,
    logoUrl: logoPreviewUrl ?? "",
    faviconUrl: faviconPreviewUrl,
    primary: draft.primary,
    background: draft.background,
    text: draft.text,
    tagline: draft.tagline,
    termsUrl: draft.termsUrl,
    supportEmail: draft.supportEmail || null,
  };

  const problems: string[] = [];
  if (!isHttpsUrl(draft.termsUrl))
    problems.push("Enter a terms URL starting with https://.");
  if (!colorsValid) problems.push("Enter all colours in #RRGGBB format.");
  if (textContrast !== null && textContrast < MIN_TEXT_CONTRAST)
    problems.push("Text needs at least 4.5:1 contrast with the background.");
  if (buttonContrast !== null && buttonContrast < MIN_LARGE_TEXT_CONTRAST)
    problems.push(
      "Choose a darker or lighter primary colour so button text is readable.",
    );
  if (!logoFile && !detail.activeTheme)
    problems.push("Choose a logo for the first version.");

  const selectFile = (kind: "logo" | "favicon") => (file: File | null) => {
    setFileError(null);
    if (!file) return;

    const isLogo = kind === "logo";
    const allowedTypes = isLogo ? LOGO_TYPES : FAVICON_TYPES;
    const maxBytes = isLogo ? MAX_LOGO_BYTES : MAX_FAVICON_BYTES;
    const isIco = !isLogo && file.name.toLowerCase().endsWith(".ico");

    if (!allowedTypes.includes(file.type) && !isIco) {
      setFileError(
        isLogo
          ? "Logo must be a PNG, JPEG or WebP image. SVG is not supported."
          : "Favicon must be a PNG or ICO image.",
      );
      return;
    }
    if (file.size > maxBytes) {
      setFileError(
        `${isLogo ? "Logo" : "Favicon"} must be ${maxBytes / 1000} KB or smaller.`,
      );
      return;
    }

    if (kind === "logo") {
      setLogoFile(file);
    } else {
      setFaviconFile(file);
      setRemoveFavicon(false);
    }
  };

  const publish = useMutation({
    mutationFn: () =>
      affiliateAdminApi.publishBranding(detail.affiliateId, draft, {
        logo: logoFile,
        favicon: faviconFile,
        removeFavicon,
      }),
    onSuccess: (result) => {
      setLastPublished(result);
      setLogoFile(null);
      setFaviconFile(null);
      setRemoveFavicon(false);
      onPublished();
    },
  });

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Typography variant="h6" component="h2">
        Branding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Publishing creates a new version.
      </Typography>

      {publish.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {apiErrorMessage(publish.error, "Branding could not be published.")}
        </Alert>
      )}
      {lastPublished && (
        <Alert
          severity="success"
          role="status"
          sx={{ mb: 2 }}
          onClose={() => setLastPublished(null)}
        >
          Published version {lastPublished.version}.
          {lastPublished.warnings.map((warning) => (
            <Typography key={warning} variant="body2" sx={{ mt: 0.5 }}>
              {warning}
            </Typography>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2.5}>
            <TextField
              label="Tagline"
              value={draft.tagline}
              onChange={(event) => update("tagline")(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              label="Terms of use URL"
              required
              placeholder="https://"
              value={draft.termsUrl}
              onChange={(event) =>
                update("termsUrl")(event.target.value.trim())
              }
            />
            <TextField
              label="Support email"
              type="email"
              value={draft.supportEmail}
              onChange={(event) =>
                update("supportEmail")(event.target.value.trim())
              }
            />

            <Stack spacing={1.5}>
              <ColorField
                label="Primary colour"
                value={draft.primary}
                onChange={update("primary")}
              />
              <ColorField
                label="Background colour"
                value={draft.background}
                onChange={update("background")}
              />
              <ColorField
                label="Text colour"
                value={draft.text}
                onChange={update("text")}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ContrastChip
                  label="Text"
                  ratio={textContrast}
                  minimum={MIN_TEXT_CONTRAST}
                />
                <ContrastChip
                  label="Buttons"
                  ratio={buttonContrast}
                  minimum={MIN_TEXT_CONTRAST}
                />
                <ContrastChip
                  label="Links"
                  ratio={accentContrast}
                  minimum={MIN_LARGE_TEXT_CONTRAST}
                />
              </Stack>
            </Stack>

            {fileError && (
              <Alert severity="error" role="alert">
                {fileError}
              </Alert>
            )}
            <ImagePicker
              label="Logo"
              helper="PNG, JPEG or WebP, up to 512 KB. Shown in the header in place of the name, on a white plate."
              accept={LOGO_TYPES.join(",")}
              previewUrl={logoPreviewUrl}
              onSelect={selectFile("logo")}
            />
            <Box>
              <ImagePicker
                label="Favicon (optional)"
                helper="PNG or ICO, up to 100 KB."
                accept={[...FAVICON_TYPES, ".ico"].join(",")}
                previewUrl={faviconPreviewUrl}
                onSelect={selectFile("favicon")}
              />
              {(detail.activeTheme?.faviconUrl || faviconFile) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={removeFavicon}
                      onChange={(event) => {
                        setRemoveFavicon(event.target.checked);
                        if (event.target.checked) setFaviconFile(null);
                      }}
                    />
                  }
                  label="Remove favicon"
                />
              )}
            </Box>

            {problems.length > 0 && (
              <Alert severity="info">
                {problems.map((problem) => (
                  <Typography key={problem} variant="body2">
                    {problem}
                  </Typography>
                ))}
              </Alert>
            )}
            <Box>
              <Button
                variant="contained"
                size="large"
                disabled={problems.length > 0 || publish.isPending}
                onClick={() => publish.mutate()}
              >
                {publish.isPending ? "Publishing…" : "Publish new version"}
              </Button>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
            Live preview
          </Typography>
          <Box
            sx={{
              position: { md: "sticky" },
              top: { md: 16 },
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
            }}
            aria-label="Sign-in page preview"
          >
            {previewTheme ? (
              <ThemeProvider theme={previewTheme}>
                <AffiliateAuthShell
                  variant="preview"
                  theme={previewBranding}
                  title="Sign in"
                >
                  <SignInForm
                    busy={false}
                    error={null}
                    info={null}
                    autoFocus={false}
                    onSubmit={noop}
                    onForgotPassword={noop}
                    onCreateAccount={noop}
                  />
                </AffiliateAuthShell>
              </ThemeProvider>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  Enter valid colours to see the preview.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
